/**
 * Shared region data for billing-address pickers across the monorepo
 * (admin frontend + public website). Static reference data — codes are
 * ISO 3166-2 and don't change.
 *
 * Imported via relative path from both packages; each package's
 * tsconfig adds `../shared/**\/*.ts` to its `include` so TypeScript
 * picks the file up at lint/build time. No workspace package needed.
 */

export interface Region {
  /** ISO 3166-2 subdivision code without the country prefix (e.g. "CA", "ON"). */
  code: string;
  name: string;
}

/**
 * US states + DC + populated territories.
 * Order convention: PR sits first (TWM customer base lives there);
 * remainder alphabetical by name. Include DC and the 5 inhabited
 * territories (AS, GU, MP, VI, UM) along with the 50 states.
 *
 * TODO: paste data here.
 */
export const US_STATES: Region[] = [
  { code: 'PR', name: 'Puerto Rico' },
  { code: 'AL', name: 'Alabama' },
  { code: 'AK', name: 'Alaska' },
  { code: 'AZ', name: 'Arizona' },
  { code: 'AR', name: 'Arkansas' },
  { code: 'CA', name: 'California' },
  { code: 'CO', name: 'Colorado' },
  { code: 'CT', name: 'Connecticut' },
  { code: 'DE', name: 'Delaware' },
  { code: 'DC', name: 'District of Columbia' },
  { code: 'FL', name: 'Florida' },
  { code: 'GA', name: 'Georgia' },
  { code: 'HI', name: 'Hawaii' },
  { code: 'ID', name: 'Idaho' },
  { code: 'IL', name: 'Illinois' },
  { code: 'IN', name: 'Indiana' },
  { code: 'IA', name: 'Iowa' },
  { code: 'KS', name: 'Kansas' },
  { code: 'KY', name: 'Kentucky' },
  { code: 'LA', name: 'Louisiana' },
  { code: 'ME', name: 'Maine' },
  { code: 'MD', name: 'Maryland' },
  { code: 'MA', name: 'Massachusetts' },
  { code: 'MI', name: 'Michigan' },
  { code: 'MN', name: 'Minnesota' },
  { code: 'MS', name: 'Mississippi' },
  { code: 'MO', name: 'Missouri' },
  { code: 'MT', name: 'Montana' },
  { code: 'NE', name: 'Nebraska' },
  { code: 'NV', name: 'Nevada' },
  { code: 'NH', name: 'New Hampshire' },
  { code: 'NJ', name: 'New Jersey' },
  { code: 'NM', name: 'New Mexico' },
  { code: 'NY', name: 'New York' },
  { code: 'NC', name: 'North Carolina' },
  { code: 'ND', name: 'North Dakota' },
  { code: 'OH', name: 'Ohio' },
  { code: 'OK', name: 'Oklahoma' },
  { code: 'OR', name: 'Oregon' },
  { code: 'PA', name: 'Pennsylvania' },
  { code: 'RI', name: 'Rhode Island' },
  { code: 'SC', name: 'South Carolina' },
  { code: 'SD', name: 'South Dakota' },
  { code: 'TN', name: 'Tennessee' },
  { code: 'TX', name: 'Texas' },
  { code: 'UT', name: 'Utah' },
  { code: 'VT', name: 'Vermont' },
  { code: 'VA', name: 'Virginia' },
  { code: 'WA', name: 'Washington' },
  { code: 'WV', name: 'West Virginia' },
  { code: 'WI', name: 'Wisconsin' },
  { code: 'WY', name: 'Wyoming' },
  { code: 'AS', name: 'American Samoa' },
  { code: 'GU', name: 'Guam' },
  { code: 'MP', name: 'Northern Mariana Islands' },
  { code: 'VI', name: 'U.S. Virgin Islands' },
];

/**
 * Canadian provinces + territories. ISO 3166-2:CA codes (13 entries).
 *
 * TODO: paste data here.
 */
export const CA_PROVINCES: Region[] = [
  { code: 'ON', name: 'Ontario' },
  { code: 'QC', name: 'Quebec' },
  { code: 'BC', name: 'British Columbia' },
  { code: 'AB', name: 'Alberta' },
  { code: 'MB', name: 'Manitoba' },
  { code: 'SK', name: 'Saskatchewan' },
  { code: 'NS', name: 'Nova Scotia' },
  { code: 'NB', name: 'New Brunswick' },
  { code: 'NL', name: 'Newfoundland and Labrador' },
  { code: 'PE', name: 'Prince Edward Island' },
  { code: 'YT', name: 'Yukon' },
  { code: 'NT', name: 'Northwest Territories' },
  { code: 'NU', name: 'Nunavut' },
];

/**
 * Code → full name lookup. Combined view across countries; collisions
 * (none in practice — US/CA codes don't overlap) would resolve to the
 * later spread.
 */
export const REGION_NAMES: Record<string, string> = Object.fromEntries(
  [...US_STATES, ...CA_PROVINCES].map((r) => [r.code, r.name]),
);

/** UI label for the region field. */
export const regionLabelFor = (country: string): 'State' | 'Province' | 'Region' => {
  if (country === 'United States') return 'State';
  if (country === 'Canada') return 'Province';
  return 'Region';
};

/** UI label for the postal-code field. */
export const postalCodeLabelFor = (country: string): 'ZIP Code' | 'Postal Code' => {
  return country === 'United States' ? 'ZIP Code' : 'Postal Code';
};
